// src/Layouts/useDynamicMenu.ts
// Sinh menu sidebar động từ API GET /system-api/Module/menu (cây module 3 tầng, đã lọc theo quyền user).
// Xem thiết kế: LMS-APP/lmsapp-service/.features/quan-ly-module/menu-structure.md
//
// Output có đúng shape mà VerticalLayouts/index.tsx đang render (isHeader / subItems / plain leaf)
// để không phải đổi JSX của sidebar — chỉ đổi nguồn dữ liệu từ file tĩnh LayoutMenuData sang API.

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getUserMenu } from 'helpers/fakebackend_helper';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('layout_use_dynamic_menu');

export interface MenuNode {
  id: number;
  code: string;
  name: string;
  url?: string | null;
  icon?: string | null;
  displayOrder: number;
  children: MenuNode[];
}

// Icon mặc định khi module chưa gán icon riêng — giữ layout không bị lệch do thiếu <i>
const DEFAULT_ICON = 'ri-file-list-2-line';

// Tìm mã module tầng-2 chứa 1 link (tầng 2 dạng lá hoặc tầng 3 bên trong) khớp đúng pathname hiện
// tại — dùng để tự mở đúng nhóm chứa trang đang xem khi menu vừa tải xong hoặc khi điều hướng bằng
// URL trực tiếp (không qua click sidebar). Trước đây việc "mở đúng nhóm" được làm bằng thao tác DOM
// thủ công trong VerticalLayouts (activateParentDropdown) — độc lập hoàn toàn với state `openCode`
// bên dưới, khiến 2 cơ chế lệch pha và nhiều menu cùng hiện "mở" (bug mục 8.a, README_UPDATE.md).
const findGroupCodeForPath = (tree: MenuNode[], pathname: string): string | null => {
  for (const root of tree) {
    for (const level2 of root.children || []) {
      if (level2.children && level2.children.length > 0) {
        if (level2.children.some((level3) => level3.url === pathname)) {
          return level2.code;
        }
      }
    }
  }
  return null;
};

const buildNavItems = (
  tree: MenuNode[],
  openCode: string | null,
  pathname: string,
  toggleOpen: (code: string) => void,
): any[] => {
  const items: any[] = [];

  tree.forEach((root) => {
    const hasChildren = root.children && root.children.length > 0;

    if (hasChildren) {
      // Tầng 1 (Group): tiêu đề section, không click được — con của nó (tầng 2) nối tiếp ngay sau
      items.push({ isHeader: true, label: root.name });

      root.children.forEach((level2) => {
        const level2HasChildren = level2.children && level2.children.length > 0;

        if (level2HasChildren) {
          // Tầng 2 dạng nhánh: có icon + mũi tên mở rộng, chứa các link tầng 3 bên trong. `active`
          // tự tính từ pathname (thay vì bị DOM gán tay) để CSS "active" luôn khớp đúng state React.
          items.push({
            id: level2.code,
            label: level2.name,
            icon: level2.icon || DEFAULT_ICON,
            link: '/#',
            active: openCode === level2.code,
            click: (e: any) => {
              e.preventDefault();
              toggleOpen(level2.code);
            },
            stateVariables: openCode === level2.code,
            // BE (GetUserMenuQueryHandler) đã OrderBy DisplayOrder/Name sẵn ở mọi tầng
            subItems: level2.children.map((level3) => ({
                id: level3.code,
                label: level3.name,
                link: level3.url || '/#',
                active: level3.url === pathname,
              })),
          });
        } else {
          // Tầng 2 dạng lá: link điều hướng trực tiếp
          items.push({
            id: level2.code,
            label: level2.name,
            icon: level2.icon || DEFAULT_ICON,
            link: level2.url || '/#',
            active: level2.url === pathname,
          });
        }
      });
    } else {
      // Root không có con (hiếm, lệch quy ước 3 tầng) — vẫn phải render được, không thể làm tiêu đề
      // vì tiêu đề không click được trong khi node này có Url thật.
      items.push({
        id: root.code,
        label: root.name,
        icon: root.icon || DEFAULT_ICON,
        link: root.url || '/#',
        active: root.url === pathname,
      });
    }
  });

  return items;
};

export function useDynamicMenu() {
  const location = useLocation();
  const [tree, setTree] = useState<MenuNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openCode, setOpenCode] = useState<string | null>(null);

  const toggleOpen = useCallback((code: string) => {
    setOpenCode((prev) => (prev === code ? null : code));
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response: any = await getUserMenu();
        const data = response.data || response || [];
        if (!cancelled) {
          setTree(data);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || st('khong_the_tai_menu_text'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Tự mở đúng nhóm tầng-2 chứa trang đang xem — chỉ set khi thực sự tìm thấy nhóm khớp
  // pathname (VD lúc F5/mở link trực tiếp), không đụng tới openCode khi người dùng đang tự
  // click mở nhóm khác không chứa trang hiện tại.
  useEffect(() => {
    const matchedGroup = findGroupCodeForPath(tree, location.pathname);
    if (matchedGroup) {
      setOpenCode(matchedGroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, location.pathname]);

  // Memoize — nếu không, mảng navItems sẽ là reference mới ở MỌI lần render (kể cả những render
  // không liên quan đến menu), khiến useEffect ở VerticalLayouts (deps có navData) chạy lại liên
  // tục và gọi window.scrollTo(0), làm trang tự cuộn lên đầu mỗi khi có bất kỳ re-render nào
  // (VD: khi scroll qua ngưỡng 50px làm Layouts đổi headerClass) — người dùng không vuốt xuống được.
  const navItems = useMemo(
    () => buildNavItems(tree, openCode, location.pathname, toggleOpen),
    [tree, openCode, location.pathname, toggleOpen],
  );

  return { navItems, loading, error };
}
